/**
 * Framer-style mobile nav: overlay spring (stiffness 300 / damping 90 / mass 1),
 * staggered link motion, body lock, Menu/Close aria label.
 * Forces data-menu-style=drawer for all ≤1199 (fixes tablet Menu on non-touch).
 */
(function () {
  const details = document.querySelector('#Details-menu-drawer-container');
  if (!details) return;

  const summary = details.querySelector('summary.header__icon--menu');
  const header = document.querySelector('#header-component');
  const MENU_LABEL = 'Menu';
  const CLOSE_LABEL = 'Close';
  const MOBILE_MENU = window.matchMedia('(max-width: 1199px)');

  /** Framer component transition (physics spring) */
  const SPRING = { stiffness: 300, damping: 90, mass: 1 };
  const LINK_RISE_PX = 8;
  const LINK_STAGGER_MS = 60;
  const LINK_LEAD_MS = 40;
  const CLOSE_FALLBACK_MS = 900;
  const OPEN_FALLBACK_MS = 1500;
  const SPRING_EPSILON = 0.02;
  /*
   * Closed-form step. Forward Euler of this spring (k=300, c=90) diverges
   * once a frame is longer than ~21ms, so opacity swings through huge
   * negative and positive values. That is a white-sheet strobe on 30fps
   * simulators, Low Power Mode, and any iPhone that drops frames while the
   * drawer opens. The analytic step stays on the same curve at any dt.
   */
  const SPRING_OMEGA0 = Math.sqrt(SPRING.stiffness / SPRING.mass);
  const SPRING_ZETA = SPRING.damping / (2 * Math.sqrt(SPRING.stiffness * SPRING.mass));
  const SPRING_OMEGA =
    SPRING_ZETA < 1
      ? SPRING_OMEGA0 * Math.sqrt(1 - SPRING_ZETA * SPRING_ZETA)
      : SPRING_OMEGA0 * Math.sqrt(Math.max(0, SPRING_ZETA * SPRING_ZETA - 1));

  let motionGeneration = 0;
  let closeFallbackTimer = 0;
  let openFallbackTimer = 0;
  /** @type {Set<number>} */
  const scheduledFrames = new Set();

  /** Horizon otherwise keeps style=menu on desktop-resized tablet and hides the drawer. */
  function forceMenuStyle() {
    if (!header) return;
    if (MOBILE_MENU.matches) {
      if (header.dataset.menuStyle !== 'drawer') {
        header.dataset.menuStyle = 'drawer';
      }
    } else if (header.dataset.menuStyle === 'drawer') {
      // Restore Horizon desktop inline nav mode above 1199
      header.dataset.menuStyle = 'menu';
    }
  }

  forceMenuStyle();
  MOBILE_MENU.addEventListener('change', forceMenuStyle);
  window.addEventListener('resize', forceMenuStyle, { passive: true });
  // Re-apply after Horizon's setHeaderMenuStyle (rAF + overflow-list)
  window.requestAnimationFrame(() => {
    forceMenuStyle();
    window.setTimeout(forceMenuStyle, 50);
    window.setTimeout(forceMenuStyle, 200);
    window.setTimeout(forceMenuStyle, 600);
  });

  // If Horizon flips style back to menu ≤1199, force drawer again
  if (header) {
    new MutationObserver(() => {
      if (!MOBILE_MENU.matches) return;
      if (header.dataset.menuStyle !== 'drawer') {
        header.dataset.menuStyle = 'drawer';
      }
    }).observe(header, { attributes: true, attributeFilter: ['data-menu-style'] });
  }

  const isMenuOpen = () => details.classList.contains('menu-open');
  const isNavActive = () =>
    details.classList.contains('menu-open') || details.classList.contains('wbs-menu-closing');
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function cancelMotion() {
    motionGeneration += 1;
    for (const id of scheduledFrames) cancelAnimationFrame(id);
    scheduledFrames.clear();
    window.clearTimeout(closeFallbackTimer);
    window.clearTimeout(openFallbackTimer);
  }

  function finishOpenMotion(drawer, generation) {
    if (generation !== motionGeneration) return;
    motionGeneration += 1;
    window.clearTimeout(openFallbackTimer);
    for (const id of scheduledFrames) cancelAnimationFrame(id);
    scheduledFrames.clear();
    clearMotionStyles(drawer);
    header?.classList.remove('wbs-menu-spring');
  }

  /**
   * Advance a damped spring by dt seconds without accumulating integration error.
   * @param {number} value
   * @param {number} velocity
   * @param {number} target
   * @param {number} dt
   */
  function stepSpring(value, velocity, target, dt) {
    const t = Math.min(Math.max(dt, 0), 0.1);
    if (t === 0) return { value, velocity };

    const x0 = value - target;
    const v0 = velocity;
    const decay = Math.exp(-SPRING_ZETA * SPRING_OMEGA0 * t);

    if (Math.abs(SPRING_ZETA - 1) < 1e-4) {
      const A = x0;
      const B = v0 + SPRING_OMEGA0 * x0;
      const x = decay * (A + B * t);
      const v = decay * (B - SPRING_OMEGA0 * (A + B * t));
      return { value: x + target, velocity: v };
    }

    if (SPRING_ZETA < 1) {
      const cos = Math.cos(SPRING_OMEGA * t);
      const sin = Math.sin(SPRING_OMEGA * t);
      const A = x0;
      const B = (v0 + SPRING_ZETA * SPRING_OMEGA0 * x0) / SPRING_OMEGA;
      const x = decay * (A * cos + B * sin);
      const v = -SPRING_ZETA * SPRING_OMEGA0 * x + decay * SPRING_OMEGA * (-A * sin + B * cos);
      return { value: x + target, velocity: v };
    }

    const cosh = Math.cosh(SPRING_OMEGA * t);
    const sinh = Math.sinh(SPRING_OMEGA * t);
    const A = x0;
    const B = (v0 + SPRING_ZETA * SPRING_OMEGA0 * x0) / SPRING_OMEGA;
    const x = decay * (A * cosh + B * sinh);
    const v = -SPRING_ZETA * SPRING_OMEGA0 * x + decay * SPRING_OMEGA * (A * sinh + B * cosh);
    return { value: x + target, velocity: v };
  }

  function springAnimate({ from, to, onUpdate, onComplete, generation }) {
    let value = from;
    let velocity = 0;
    let last = performance.now();
    let frameId = 0;

    const tick = (now) => {
      scheduledFrames.delete(frameId);
      if (generation !== motionGeneration) return;

      const dt = Math.min(Math.max((now - last) / 1000, 0), 0.1);
      last = now;
      const next = stepSpring(value, velocity, to, dt);
      value = next.value;
      velocity = next.velocity;

      if (
        !Number.isFinite(value) ||
        !Number.isFinite(velocity) ||
        (Math.abs(value - to) < SPRING_EPSILON && Math.abs(velocity) < SPRING_EPSILON)
      ) {
        onUpdate(to);
        onComplete?.();
        return;
      }

      onUpdate(value);
      frameId = requestAnimationFrame(tick);
      scheduledFrames.add(frameId);
    };

    frameId = requestAnimationFrame(tick);
    scheduledFrames.add(frameId);
  }

  function runSpringPair(fromOpacity, toOpacity, fromY, toY, onUpdate, generation) {
    return new Promise((resolve) => {
      if (generation !== motionGeneration) {
        resolve();
        return;
      }

      let done = 0;
      const check = () => {
        if (generation !== motionGeneration) {
          resolve();
          return;
        }
        done += 1;
        if (done >= 2) resolve();
      };

      springAnimate({
        from: fromOpacity,
        to: toOpacity,
        onUpdate: (opacity) => onUpdate({ opacity }),
        onComplete: check,
        generation,
      });

      if (fromY === toY) {
        check();
      } else {
        springAnimate({
          from: fromY,
          to: toY,
          onUpdate: (y) => onUpdate({ y }),
          onComplete: check,
          generation,
        });
      }
    });
  }

  function animationIndex(el) {
    let node = el;
    while (node) {
      const raw = node.style?.getPropertyValue?.('--menu-drawer-animation-index');
      if (raw) {
        const value = Number.parseFloat(raw);
        if (Number.isFinite(value)) return value;
      }
      node = node.parentElement;
    }
    return 0;
  }

  function clamp01(value) {
    if (!Number.isFinite(value)) return 0;
    return Math.min(1, Math.max(0, value));
  }

  function clampY(value) {
    if (!Number.isFinite(value)) return 0;
    return Math.min(32, Math.max(-32, value));
  }

  function readOpacity(el) {
    const raw = el?.style?.opacity;
    if (raw) {
      const value = Number.parseFloat(raw);
      if (Number.isFinite(value)) return clamp01(value);
    }
    return 1;
  }

  function getAnimatedLinks(drawer) {
    return [...drawer.querySelectorAll('.menu-drawer__animated-element')].sort(
      (a, b) => animationIndex(a) - animationIndex(b)
    );
  }

  function clearMotionStyles(drawer) {
    if (!drawer) return;
    drawer.style.removeProperty('opacity');
    drawer.style.removeProperty('transform');
    drawer.style.removeProperty('visibility');
    drawer.style.removeProperty('pointer-events');
    getAnimatedLinks(drawer).forEach((el) => {
      el.style.removeProperty('opacity');
      el.style.removeProperty('transform');
    });
  }

  function animateOpen(drawer) {
    if (!drawer || !MOBILE_MENU.matches || prefersReducedMotion()) {
      if (drawer) {
        drawer.style.removeProperty('opacity');
        drawer.style.removeProperty('transform');
        drawer.style.removeProperty('visibility');
      }
      header?.classList.remove('wbs-menu-spring');
      return Promise.resolve();
    }

    const generation = ++motionGeneration;
    header?.classList.add('wbs-menu-spring');
    // Keep the sheet invisible before any measurement. A forced layout while
    // opacity is still 1 paints the white drawer for a frame on iOS.
    drawer.style.opacity = '0';
    drawer.style.visibility = 'visible';
    drawer.style.transform = 'translateY(0px)';
    clearMotionStyles(drawer);
    drawer.style.opacity = '0';
    drawer.style.visibility = 'visible';

    openFallbackTimer = window.setTimeout(() => {
      finishOpenMotion(drawer, generation);
    }, OPEN_FALLBACK_MS);

    const links = getAnimatedLinks(drawer);
    links.forEach((el) => {
      el.style.opacity = '0';
      el.style.transform = `translateY(${LINK_RISE_PX}px)`;
    });

    // Framer panel: white overlay fades in with the same spring
    const drawerPromise = runSpringPair(0, 1, 0, 0, ({ opacity }) => {
      if (opacity !== undefined) drawer.style.opacity = String(clamp01(opacity));
    }, generation);

    if (links.length === 0) {
      return drawerPromise.then(() => finishOpenMotion(drawer, generation));
    }

    const linkPromises = links.map(
      (el, index) =>
        new Promise((resolve) => {
          setTimeout(() => {
            if (generation !== motionGeneration) {
              resolve();
              return;
            }

            const applyLink = ({ opacity, y } = {}) => {
              if (opacity !== undefined) el.style.opacity = String(clamp01(opacity));
              if (y !== undefined) el.style.transform = `translateY(${clampY(y)}px)`;
            };
            applyLink({ opacity: 0, y: LINK_RISE_PX });
            runSpringPair(0, 1, LINK_RISE_PX, 0, applyLink, generation).then(resolve);
          }, LINK_LEAD_MS + index * LINK_STAGGER_MS);
        })
    );

    return Promise.all([drawerPromise, ...linkPromises]).then(() => {
      finishOpenMotion(drawer, generation);
    });
  }

  function animateClose(drawer, onComplete) {
    const finish = () => {
      window.clearTimeout(closeFallbackTimer);
      onComplete?.();
    };

    if (!drawer || !MOBILE_MENU.matches || prefersReducedMotion()) {
      cancelMotion();
      clearMotionStyles(drawer);
      finish();
      return;
    }

    const generation = ++motionGeneration;
    header?.classList.add('wbs-menu-spring');

    closeFallbackTimer = window.setTimeout(() => {
      if (generation !== motionGeneration) return;
      motionGeneration += 1;
      for (const id of scheduledFrames) cancelAnimationFrame(id);
      scheduledFrames.clear();
      clearMotionStyles(drawer);
      header?.classList.remove('wbs-menu-spring');
      finish();
    }, CLOSE_FALLBACK_MS);

    const links = getAnimatedLinks(drawer);
    const drawerOpacity = readOpacity(drawer);

    const linkPromises = links.map((el) => {
      const startOpacity = readOpacity(el);
      return runSpringPair(startOpacity, 0, 0, LINK_RISE_PX, ({ opacity, y } = {}) => {
        if (opacity !== undefined) el.style.opacity = String(clamp01(opacity));
        if (y !== undefined) el.style.transform = `translateY(${clampY(y)}px)`;
      }, generation);
    });

    drawer.style.opacity = String(drawerOpacity);
    drawer.style.transform = 'translateY(0px)';

    const drawerPromise = runSpringPair(drawerOpacity, 0, 0, 0, ({ opacity } = {}) => {
      if (opacity !== undefined) drawer.style.opacity = String(clamp01(opacity));
    }, generation);

    Promise.all([drawerPromise, ...linkPromises]).then(() => {
      if (generation !== motionGeneration) return;
      clearMotionStyles(drawer);
      header?.classList.remove('wbs-menu-spring');
      finish();
    });
  }

  window.__wbsMenuSpring = { animateOpen, animateClose, cancelMotion };

  details.addEventListener('toggle', (event) => {
    // Child accordions bubble toggle. Only the drawer details is controlled here.
    if (event.target !== details) return;
    if (!isNavActive()) details.removeAttribute('open');
  });

  let wasActive = false;

  const sync = () => {
    const open = isMenuOpen();
    const active = isNavActive();
    document.body.classList.toggle('wbs-nav-open', active);
    header?.classList.toggle('wbs-nav-open', active);
    if (summary) {
      summary.setAttribute('aria-label', open ? CLOSE_LABEL : MENU_LABEL);
    }
  };

  const observer = new MutationObserver(() => {
    const active = isNavActive();
    if (!active && wasActive) {
      header?.classList.remove('wbs-menu-spring');
    }
    wasActive = active;
    sync();
  });

  observer.observe(details, { attributes: true, attributeFilter: ['class', 'open'] });

  sync();
})();

/**
 * Header search replaces the bar. No modal, no suggestions.
 * Submit goes to the search results page.
 */
(function () {
  const header = document.querySelector('#header-component');
  const form = header && header.querySelector('[data-wbs-header-search]');
  if (!header || !form) return;

  const input = form.querySelector('input[name="q"]');
  const closeButton = form.querySelector('[data-wbs-header-search-close]');

  function closeMenu() {
    const details = document.querySelector('#Details-menu-drawer-container');
    if (!details || !details.classList.contains('menu-open')) return;
    const summary = details.querySelector('summary.header__icon--menu');
    if (summary) summary.click();
  }

  function openSearch() {
    closeMenu();
    header.classList.add('is-wbs-search-open');
    form.hidden = false;
    window.setTimeout(() => {
      if (input) input.focus();
    }, 0);
  }

  function closeSearch() {
    header.classList.remove('is-wbs-search-open');
    form.hidden = true;
    if (input) input.value = '';
  }

  header.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-wbs-header-search-open]');
    if (!trigger || !header.contains(trigger)) return;
    event.preventDefault();
    openSearch();
  });

  if (closeButton) {
    closeButton.addEventListener('click', closeSearch);
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (!header.classList.contains('is-wbs-search-open')) return;
    closeSearch();
  });
})();

/**
 * Homepage: keep the transparent header until the WBS hero scrolls past the bar.
 * Horizon otherwise flips to solid as soon as sticky-state becomes active (y > 0).
 */
(function () {
  const header = document.querySelector('#header-component');
  if (!header || header.getAttribute('transparent') !== 'not-sticky') return;

  const hero = document.querySelector('[data-wbs-hero]');
  if (!hero) return;

  const heroEdge = hero.closest('.shopify-section') || hero;
  const desktopScroll = window.matchMedia('(min-width: 990px)');
  let scrollTarget = null;
  let boundLenis = null;
  let raf = 0;
  let overHero = null;

  function setOverHero(next) {
    if (overHero === next) return;
    overHero = next;
    if (next) {
      header.setAttribute('data-wbs-over-hero', '');
      header.removeAttribute('data-wbs-past-hero');
    } else {
      header.removeAttribute('data-wbs-over-hero');
      header.setAttribute('data-wbs-past-hero', '');
    }
  }

  function update() {
    raf = 0;
    const headerBottom = header.getBoundingClientRect().bottom;
    const heroBottom = heroEdge.getBoundingClientRect().bottom;
    setOverHero(heroBottom > headerBottom + 0.5);
  }

  function onScroll() {
    if (raf) return;
    raf = requestAnimationFrame(update);
  }

  function bindScrollTarget() {
    const next = desktopScroll.matches
      ? document.querySelector('.page-wrapper') || window
      : window;
    if (scrollTarget === next) return;
    if (scrollTarget) scrollTarget.removeEventListener('scroll', onScroll);
    scrollTarget = next;
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
  }

  function bindLenis() {
    if (!window.lenis || boundLenis === window.lenis) return;
    if (boundLenis) boundLenis.off('scroll', onScroll);
    boundLenis = window.lenis;
    window.lenis.on('scroll', onScroll);
  }

  bindScrollTarget();
  bindLenis();
  update();

  window.addEventListener('resize', onScroll, { passive: true });
  desktopScroll.addEventListener('change', () => {
    bindScrollTarget();
    onScroll();
  });
  window.addEventListener('load', () => {
    bindLenis();
    update();
  });
})();
